import { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Trading hours: 4am–8pm ET (hours 4–20)
const HOURS = Array.from({ length: 17 }, (_, i) => i + 4);

function formatHour(h) {
  if (h === 0) return '12am';
  if (h < 12) return `${h}am`;
  if (h === 12) return '12pm';
  return `${h - 12}pm`;
}

function getCellColor(avgPnl, count) {
  if (!count) return 'bg-slate-800/30';
  if (avgPnl > 200) return 'bg-emerald-500/80';
  if (avgPnl > 100) return 'bg-emerald-500/60';
  if (avgPnl > 50)  return 'bg-emerald-500/40';
  if (avgPnl > 0)   return 'bg-emerald-500/20';
  if (avgPnl > -50) return 'bg-red-500/20';
  if (avgPnl > -100) return 'bg-red-500/40';
  if (avgPnl > -200) return 'bg-red-500/60';
  return 'bg-red-500/80';
}

function periodToDateRange(period, appliedCustomDates) {
  const now = new Date();
  switch (period) {
    case 'week': { const d = new Date(now); d.setDate(d.getDate() - 7); return { dateFrom: d.toISOString(), dateTo: now.toISOString() }; }
    case 'month': { const d = new Date(now); d.setDate(d.getDate() - 30); return { dateFrom: d.toISOString(), dateTo: now.toISOString() }; }
    case 'year':
    case 'ytd': { const d = new Date(now.getFullYear(), 0, 1); return { dateFrom: d.toISOString(), dateTo: now.toISOString() }; }
    case 'all': return { dateFrom: null, dateTo: null };
    case 'custom': return {
      dateFrom: appliedCustomDates?.start ? new Date(appliedCustomDates.start).toISOString() : null,
      dateTo: appliedCustomDates?.end ? new Date(appliedCustomDates.end).toISOString() : now.toISOString(),
    };
    default: { const d = new Date(now); d.setDate(d.getDate() - 30); return { dateFrom: d.toISOString(), dateTo: now.toISOString() }; }
  }
}

const TimeHeatmap = ({ period, appliedCustomDates }) => {
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { dateFrom, dateTo } = periodToDateRange(period, appliedCustomDates);
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const { data } = await api.get(`/analytics/time-heatmap?${params.toString()}`);
      if (data.success) setCells(data.data || []);
    } catch (err) {
      console.error('TimeHeatmap fetch error:', err.message);
    } finally {
      setLoading(false);
    }
  }, [period, appliedCustomDates]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build lookup map: "day-hour" → cell data
  const cellMap = {};
  cells.forEach(c => { cellMap[`${c.day}-${c.hour}`] = c; });

  if (loading) {
    return (
      <div className="animate-pulse bg-slate-800/50 rounded-xl h-64" />
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
      <h3 className="text-slate-200 font-semibold mb-4 flex items-center gap-2">
        <span>🕐</span> Time-of-Day Performance
        <span className="text-xs text-slate-400 font-normal ml-auto">Eastern Time</span>
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr>
              <th className="w-10 text-slate-500 font-medium pb-2 text-right pr-2">ET</th>
              {[1, 2, 3, 4, 5].map(d => (
                <th key={d} className="text-slate-400 font-medium pb-2 text-center">
                  {DAYS[d]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HOURS.map(hour => (
              <tr key={hour}>
                <td className="text-slate-500 text-right pr-2 py-0.5 whitespace-nowrap">{formatHour(hour)}</td>
                {[1, 2, 3, 4, 5].map(day => {
                  const cell = cellMap[`${day}-${hour}`];
                  const colorClass = getCellColor(cell?.avgPnl ?? null, cell?.count ?? 0);
                  return (
                    <td
                      key={day}
                      className="py-0.5 px-0.5"
                      onMouseEnter={(e) => {
                        if (!cell) return;
                        setTooltip({ cell, x: e.clientX, y: e.clientY });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      <div
                        className={`w-full h-5 rounded-sm ${colorClass} cursor-default transition-opacity hover:opacity-90`}
                        style={{ minWidth: '28px' }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
        <span>Avg P&L:</span>
        {[
          { label: '-$200+', cls: 'bg-red-500/80' },
          { label: '-$50', cls: 'bg-red-500/30' },
          { label: '$0', cls: 'bg-slate-700' },
          { label: '+$50', cls: 'bg-emerald-500/30' },
          { label: '+$200+', cls: 'bg-emerald-500/80' },
        ].map(({ label, cls }) => (
          <span key={label} className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded-sm inline-block ${cls}`} />
            {label}
          </span>
        ))}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-xl text-xs pointer-events-none"
          style={{ left: tooltip.x + 12, top: tooltip.y - 60 }}
        >
          <div className="font-semibold text-slate-200 mb-1">
            {DAYS[tooltip.cell.day]} {formatHour(tooltip.cell.hour)}
          </div>
          <div className="text-slate-300">Trades: {tooltip.cell.count}</div>
          <div className={`font-medium ${tooltip.cell.avgPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            Avg P&L: {tooltip.cell.avgPnl >= 0 ? '+' : ''}${tooltip.cell.avgPnl?.toFixed(2)}
          </div>
          <div className="text-slate-400">Win rate: {tooltip.cell.winRate?.toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
};

export default TimeHeatmap;
